"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Download } from "lucide-react";
import { exportToExcel } from "@/lib/excelExport";
import { STATUS_CHART_COLORS } from "@/lib/appointmentStatus";

interface StaffInfo {
  id: string;
  name: string;
}

interface MonthlySummaryDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    summary: Array<{
      status: string;
      byStaff: { [staffName: string]: { count: number; percentage: number } };
      total: { count: number; percentage: number };
    }>;
    grandTotal: number;
  };
}

const STATUS_COLORS: Record<string, string> = {
  ...STATUS_CHART_COLORS,
  // Values that are not appointment statuses but appear in report groupings.
  FINISHED: "#059669",
  DEFAULT: "#94a3b8",
};

export function MonthlySummaryDashboard({ data }: MonthlySummaryDashboardProps) {
  const staffNames = data.staff.map((s) => s.name);

  const handleExport = () => {
    const exportData = data.summary.map((row) => {
      const rowData: Record<string, string | number> = {
        Status: row.status,
      };
      staffNames.forEach((name) => {
        const staffData = row.byStaff[name] || { count: 0, percentage: 0 };
        rowData[`${name} Count`] = staffData.count;
        rowData[`${name} %`] = `${staffData.percentage.toFixed(1)}%`;
      });
      rowData["Total Count"] = row.total.count;
      rowData["Total %"] = `${row.total.percentage.toFixed(1)}%`;
      return rowData;
    });

    exportToExcel(exportData, "Monthly_Summary", "Summary");
  };

  // Prepare data for pie chart
  const pieData = data.summary
    .filter((item) => item.total.count > 0)
    .map((item) => ({
      name: item.status,
      value: item.total.count,
    }));

  const getStatusColor = (status: string) => {
    return STATUS_COLORS[status.toUpperCase().replace(/\s+/g, "_")] || STATUS_COLORS.DEFAULT;
  };

  return (
    <div className="space-y-4">
      {/* Grand Total Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
        <CardHeader>
          <CardTitle>Grand Total Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold">{data.grandTotal}</div>
          <p className="text-sm text-muted-foreground mt-2">
            All orders in the selected period
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Status Distribution Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Order Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    percent && percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ""
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getStatusColor(entry.name)} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Summary Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Status Breakdown by Staff</CardTitle>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    {staffNames.map((name) => (
                      <TableHead key={name} className="text-right">{name}</TableHead>
                    ))}
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.summary.map((row) => (
                    <TableRow key={row.status}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: getStatusColor(row.status) }}
                          />
                          {row.status}
                        </div>
                      </TableCell>
                      {staffNames.map((name) => {
                        const staffData = row.byStaff[name] || { count: 0, percentage: 0 };
                        return (
                          <TableCell key={name} className="text-right">
                            {staffData.count}
                            <span className="text-xs text-muted-foreground ml-1">
                              ({staffData.percentage.toFixed(1)}%)
                            </span>
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right font-medium">
                        {row.total.count}
                        <span className="text-xs text-muted-foreground ml-1">
                          ({row.total.percentage.toFixed(1)}%)
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell>TOTAL</TableCell>
                    {staffNames.map((name) => (
                      <TableCell key={name} className="text-right">
                        {data.summary.reduce((sum, r) => {
                          const staffData = r.byStaff[name] || { count: 0 };
                          return sum + staffData.count;
                        }, 0)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">{data.grandTotal}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
